import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { walk } from "../../catalogue/types";
import type { SideTab } from "../../core/store";
import { useCatalogue, useInfo, useLabels, useLayers, useMapCore, useMapStore } from "../hooks";
import { selectedOverlayCount } from "../layerTreeModel";
import type { TreeRow } from "../layerTreeModel";
import { Button } from "../primitives/Button";
import { EmptyState } from "../primitives/EmptyState";
import { Input } from "../primitives/Input";
import { Modal } from "../primitives/Modal";
import { Skeleton } from "../primitives/Skeleton";
import { Tabs } from "../primitives/Tabs";
import { FeatureInspectorBody, inspectorTitle } from "./FeatureInspector";
import { LayerRow } from "./LayerRow";
import { LayerTree } from "./LayerTree";
import { PanelMenu } from "./PanelMenu";
import { PanelSearch } from "./PanelSearch";
import { ResultsPane } from "./ResultsPane";

export type SidePanelTab = SideTab;

export type SidePanelProps = { className?: string };

export function UnifiedTreeBody({ filter, onTable }: { filter: string; onTable: ((row: TreeRow) => void) | null }) {
  const core = useMapCore();
  const labels = useLabels();
  const catalogue = useCatalogue();
  const catalogueError = useMapStore(a => a.catalogueError);
  const { list } = useLayers();
  const custom = list.filter(a => a.root === "custom" || a.root === "track");

  if (catalogue === null && catalogueError !== null) {
    return <EmptyState icon="warning" title={labels.loadFailed} text={catalogueError} action={<Button kind="ghost" label={labels.retry} action="retry" onClick={() => void core.reloadCatalogue()} />} />;
  }

  if (catalogue === null) {
    return (
      <div className="fxmap-drawer-skeleton" aria-busy="true" aria-label={labels.loading}>
        <Skeleton lines={6} />
      </div>
    );
  }

  if (catalogue.maps.length === 0 && catalogue.layers.length === 0 && custom.length === 0) return <EmptyState icon="layers" title={labels.noLayers} />;

  const term = filter.trim().toLocaleLowerCase();

  return (
    <>
      <LayerTree root="all" filter={filter} label={labels.panelTitle} onTable={onTable} />
      {custom.length > 0 ? (
        <ul className="fxmap-row-list" role="list" data-fxmap-section="custom">
          {custom
            .filter(a => term.length === 0 || a.title.toLocaleLowerCase().includes(term))
            .map(snapshot => (
              <li key={snapshot.id} role="listitem">
                <LayerRow snapshot={snapshot} />
              </li>
            ))}
        </ul>
      ) : null}
    </>
  );
}

export function LayersPane({ onTable }: { onTable: ((row: TreeRow) => void) | null }) {
  const core = useMapCore();
  const labels = useLabels();
  const catalogue = useCatalogue();
  const [filter, setFilter] = useState("");
  const groupKeys = useMemo(() => (catalogue === null ? [] : [...walk(catalogue.maps), ...walk(catalogue.layers)].filter(a => a.kind === "group").map(a => `${a.root}:${a.key}`)), [catalogue]);
  const setAll = (collapsed: boolean) => {
    for (const key of groupKeys) core.layers.setCollapsed(key, collapsed);
  };

  return (
    <>
      <div className="fxmap-side-tools">
        <Input type="search" icon="filter" value={filter} placeholder={labels.filterLayers} aria-label={labels.filterLayers} onChange={event => setFilter(event.target.value)} onClear={() => setFilter("")} clearLabel={labels.close} data-fxmap="layer-search" />
        <Button kind="icon" size="sm" icon="collapseAll" label={labels.collapseAll} onClick={() => setAll(true)} />
        <Button kind="icon" size="sm" icon="expandAll" label={labels.expandAll} onClick={() => setAll(false)} />
        <PanelMenu />
      </div>
      <div className="fxmap-side-body" data-fxmap="layer-panel">
        <UnifiedTreeBody filter={filter} onTable={onTable} />
      </div>
    </>
  );
}

function PanelFrame({ children, className }: { children: ReactNode; className?: string }) {
  const labels = useLabels();

  return (
    <aside className={["fxmap-side-panel", "fxmap-card", className ?? ""].filter(Boolean).join(" ")} role="complementary" aria-label={labels.panelTitle} data-fxmap="side-panel">
      {children}
    </aside>
  );
}

export function SidePanel({ className }: SidePanelProps) {
  const core = useMapCore();
  const labels = useLabels();
  const catalogue = useCatalogue();
  const { list } = useLayers();
  const { state, api } = useInfo();
  const sideClosed = useMapStore(a => a.sideClosed);
  const inspectorModal = useMapStore(a => a.inspectorModal);
  const tab = useMapStore(a => a.sideTab);
  const resultCount = useMapStore(a => a.results.items.length);
  const infoOpen = state.kind !== "closed";
  const count = selectedOverlayCount(list, catalogue);

  if (sideClosed) return null;

  const setTab = (next: SideTab) => core.store.getState().patch({ sideTab: next });
  const controls = core.options.controls;
  const openTable = controls.table ? (row: TreeRow) => core.store.getState().patch({ tableLayer: row.id }) : null;
  const tabs = [
    ...(controls.layersTab ? [{ id: "layers" as SideTab, label: labels.layers, icon: "layers" as const, badge: count > 0 ? count : null }] : []),
    ...(controls.search.enabled ? [{ id: "results" as SideTab, label: labels.resultsTab, icon: "search" as const, badge: resultCount > 0 ? resultCount : null }] : []),
    { id: "selected" as SideTab, label: labels.selectedObject, icon: "info" as const }
  ];
  const current = tabs.some(a => a.id === tab) ? tab : tabs[0].id;
  const closePanel = () => core.store.getState().patch({ sideClosed: true });
  const title = inspectorTitle(state, labels);
  const bare = state.kind === "object" || state.kind === "tile";

  const selectedBody = infoOpen ? (
    <div className="fxmap-side-body fxmap-inspector fxmap-inspector-embedded" data-fxmap="inspector">
      <FeatureInspectorBody onClose={bare ? () => api.close() : null} />
    </div>
  ) : (
    <div className="fxmap-side-body" data-fxmap="inspector">
      <EmptyState icon="info" title={labels.noSelection} />
    </div>
  );

  return (
    <>
      <PanelFrame className={className}>
        <div className={["fxmap-side-search", controls.search.enabled ? "" : "fxmap-side-search-bare"].filter(Boolean).join(" ")}>
          {controls.search.enabled ? <PanelSearch /> : <span className="fxmap-side-title fxmap-display fxmap-truncate">{labels.panelTitle}</span>}
          <span className="fxmap-side-corner">
            {tab === "selected" && infoOpen ? <Button kind="icon" size="sm" icon="fullscreen" label={labels.openInWindow} onClick={() => core.store.getState().patch({ inspectorModal: true })} data-fxmap-action="inspector-modal" /> : null}
            <Button kind="icon" size="sm" icon="close" label={labels.closePanel} onClick={closePanel} data-fxmap-action="close" />
          </span>
        </div>
        <Tabs<SideTab>
          tabs={tabs}
          value={current}
          onChange={setTab}
          label={labels.panelTitle}
          fill
          className="fxmap-side-tabs"
          dataFxmap="side-tabs"
        >
          {() => null}
        </Tabs>
        {current === "layers" ? (
          <LayersPane onTable={openTable} />
        ) : current === "results" ? (
          <div className="fxmap-side-body" data-fxmap="results-panel">
            <ResultsPane />
          </div>
        ) : (
          selectedBody
        )}
      </PanelFrame>
      <Modal open={inspectorModal && infoOpen} title={title} onClose={() => core.store.getState().patch({ inspectorModal: false })} closeLabel={labels.close} width="min(760px, calc(100% - 32px))" headerTools={<Button kind="icon" size="sm" icon="exitFullscreen" label={labels.backToPanel} onClick={() => core.store.getState().patch({ inspectorModal: false })} />} dataFxmap="inspector-modal">
        <FeatureInspectorBody onClose={null} />
      </Modal>
    </>
  );
}

export function SidePanelOpener({ className }: { className?: string }) {
  const core = useMapCore();
  const labels = useLabels();
  const sideClosed = useMapStore(a => a.sideClosed);
  const catalogue = useCatalogue();
  const { list } = useLayers();
  const count = selectedOverlayCount(list, catalogue);

  if (!sideClosed) return null;

  return (
    <span className={["fxmap-side-opener", className ?? ""].filter(Boolean).join(" ")}>
      <Button kind="icon" size="lg" icon="layers" label={labels.openPanel} className="fxmap-card" onClick={() => core.store.getState().patch({ sideClosed: false })} data-fxmap="layer-toggle" />
      {count > 0 ? <span className="fxmap-rail-badge fxmap-tabular">{count}</span> : null}
    </span>
  );
}
